import { useMapSelection } from '@/hooks/useMapSelection';
import { useUiStore } from '@/store/uiStore';
import { useDataStore } from '@/store/dataStore';
import { featureFlags } from '@/config/featureFlags';
import { MetricCard } from './MetricCard';
import { ExplanationList } from './ExplanationList';
import { FreshnessBadge } from './FreshnessBadge';
import { ExportMenu } from './ExportMenu';
import { CompareBlock } from './CompareBlock';

/** Plain-language definitions shown on hover/focus of each metric card's (i) icon. */
const METRIC_HELP = {
  hotCold:
    "Overall market temperature versus the rest of Texas this period, from -100 (coldest) to +100 (hottest). Built from how this county's pricing and days-on-market compare statewide.",
  sellSpeedShift:
    "Whether homes here are selling faster or slower than last period, for this county specifically -- not a comparison to other counties. Falling days-on-market reads as a positive (hot) shift; rising days-on-market reads as negative (cold).",
  inventoryPressure:
    'How much supply is building up (0-100, higher = more supply, favors buyers) based on the trend in active listings and months of inventory here versus last period. A rising number means the market is loosening.',
  opportunityScore:
    'A composite 0-100 read on how favorable current conditions look for buyers in this county right now, blending price levels, inventory, and days-on-market versus the statewide benchmark.',
  coolingRisk:
    'How likely this county is to head into a cooling phase soon (0-100), based on early trend shifts -- lengthening days-on-market, rising inventory, or slowing price growth -- even if the market still looks hot today.',
  buyerCompetition:
    "How much competition buyers are facing here (0-100, higher = more competitive) versus the statewide benchmark, based on this county's pricing and days-on-market relative to other counties.",
  confidence:
    'How much statistical trust to place in this county’s scores (0-100), based on how many data fields are filled in and how many independent sources corroborate them -- not a reflection of the market itself.',
};

export function InsightDrawer() {
  const { selected, compare, isDrawerOpen } = useMapSelection();
  const closeDrawer = useUiStore((s) => s.closeDrawer);
  const geographies = useDataStore((s) => s.geographies);
  const lastRefreshedAt = useDataStore((s) => s.lastRefreshedAt);

  if (!isDrawerOpen || !selected) return null;

  const { record, insights } = selected;

  return (
    <aside className="insight-drawer" role="dialog" aria-label={`${record.geoName} County details`}>
      <div className="insight-drawer__header">
        <h2>{record.geoName} County</h2>
        <button type="button" onClick={closeDrawer} aria-label="Close" className="insight-drawer__close">
          &times;
        </button>
      </div>

      <FreshnessBadge periodLabel={record.periodLabel} isSampleData={insights.isSampleData} lastRefreshedAt={lastRefreshedAt} />

      <div className="insight-drawer__metrics">
        <MetricCard title="Hot / Cold" band={insights.hotColdScore} helpText={METRIC_HELP.hotCold} />
        <MetricCard title="Sell-Speed Shift" band={insights.sellSpeedShift} helpText={METRIC_HELP.sellSpeedShift} />
        <MetricCard
          title="Inventory Pressure"
          band={insights.inventoryPressure}
          suffix="/100"
          helpText={METRIC_HELP.inventoryPressure}
        />
        <MetricCard
          title="Opportunity"
          band={insights.opportunityScore}
          suffix="/100"
          helpText={METRIC_HELP.opportunityScore}
        />
        <MetricCard title="Cooling Risk" band={insights.coolingRisk} suffix="/100" helpText={METRIC_HELP.coolingRisk} />
        <MetricCard
          title="Buyer Competition"
          band={insights.buyerCompetition}
          suffix="/100"
          helpText={METRIC_HELP.buyerCompetition}
        />
        <MetricCard title="Confidence" band={insights.confidenceScore} suffix="/100" helpText={METRIC_HELP.confidence} />
      </div>

      <section className="insight-drawer__raw">
        <h3>Published figures</h3>
        <dl>
          <dt>Median sale price</dt>
          <dd>{record.resolved.medianSalePrice ? `$${record.resolved.medianSalePrice.toLocaleString()}` : '--'}</dd>
          <dt>Median days on market</dt>
          <dd>{record.resolved.medianDaysOnMarket ?? '--'}</dd>
          <dt>Months of inventory</dt>
          <dd>{record.resolved.monthsOfInventory ?? '--'}</dd>
          <dt>Sale-to-list ratio</dt>
          <dd>{record.resolved.saleToListRatioPct ? `${record.resolved.saleToListRatioPct}%` : '--'}</dd>
          <dt>Sources</dt>
          <dd>{insights.contributingSources.join(', ') || '--'}</dd>
        </dl>
      </section>

      <section className="insight-drawer__explanations">
        <h3>What this means</h3>
        <ExplanationList explanations={insights.explanations} />
      </section>

      {featureFlags.ENABLE_COMPARE_MODE && compare && <CompareBlock primary={selected} compare={compare} />}

      <ExportMenu allGeographies={geographies} selected={selected} />
    </aside>
  );
}
